// InlineFeedback.tsx
import { useContext, useEffect, useRef, useState } from "react";
import "./style.css";
import Numbering from "./Numbering";
import { FeedBackComponentProps, FeedbackProps } from "./types";
import Emoji from "./Emoji";
import Star from "./Star";
import Like from "./Like";
import QuestWrapper from "../../../../components/QuestWrapper";
import Success from "./Success";
import { cross } from "../../../../assets/assetsSVG";
import Loader from "../../../../components/Login/Loader";
import InlineFeedbackApi from "./InlineFeedbackApi";
import {
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
} from "../../tutorial/components/tutorial.types";
import InlineFeedbackThemeManager from "./InlineFeedbackThemeManager";
import { useGeneral } from "../../../../general";

const componentMapping: {
  [key in string]: (props: FeedBackComponentProps) => JSX.Element;
} = {
  number: Numbering,
  like: Like,
  emoji: Emoji,
  star: Star,
};

export function InlineFeedbackWrapper({
  userId,
  token,
  questId: campaignId,
  heading = "Found it helpful",
  description = "Your feedback helps us improve search results!",
  type = "number",
  uniqueUserId,
  uniqueEmailId,
  Headers,
  count = 5,
  footerText,
  footerLink,
  styleConfig,
  onChange,
  onRequestClose,
  initialState,
  variation,
  entityAuthenticationToken,
}: FeedbackProps) {
  const [questData, setQuestData] = useState<any | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [componentType, setComponentType] = useState<string>(type);
  const [brandTheme, setBrandTheme] = useState(DEFAULT_BRAND_THEME);
  const [questThemeData, setQuestThemeData] = useState(DEFAULT_QUEST_THEME);
  const [header, setHeader] = useState(Headers || []);
  const [showFooter, setShowFooter] = useState(true);

  const { themeConfig, entityId, apiType, apiKey, apiSecret, user } = useContext(
    QuestWrapper.Context
  );
  const data = useRef<object>();
  const inlineFeedbackApi = new InlineFeedbackApi(apiType);
  const general = useGeneral('quest')

  const themeManager = new InlineFeedbackThemeManager(
    themeConfig,
    styleConfig,
    questThemeData,
    brandTheme
  );

  const Component: (props: FeedBackComponentProps) => JSX.Element =
    componentMapping[componentType] || Like;

  useEffect(() => {
    general.trackEvent(
      'quest-inlinefeedback-loaded',
      (uniqueUserId || userId || user.userId)!,
      {},
      true,
      !uniqueUserId,
      true
    )
    const initializeFeedback = async () => {
      try {
        setLoading(true);

        const userAuth = await inlineFeedbackApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken,
        });

        if (!userAuth) return;

        const headers = {
          apiKey,
          apisecret: apiSecret,
          userId: userAuth.userId,
          token: userAuth.token,
        };

        const response = await inlineFeedbackApi.getFeedbackData(
          entityId,
          campaignId,
          headers,
          variation
        );

        if (response) {
          setQuestData(response);

          if (response?.data?.sdkConfig?.uiProps?.questThemeData) {
            setQuestThemeData(response?.data?.sdkConfig.uiProps.questThemeData);
            themeManager.updateQuestTheme(
              response?.data?.sdkConfig.uiProps.questThemeData
            );
          }


          if (response?.data?.sdkConfig?.uiProps?.BrandTheme) {
            const brandThemeProps =
              response?.data?.sdkConfig.uiProps.BrandTheme.properties;
            setBrandTheme(brandThemeProps);
            themeManager.updateBrandTheme(brandThemeProps);
          }

          if (response?.data?.sdkConfig?.uiProps?.Headers && (!header || header?.length === 0)) {
            setHeader(response?.data?.sdkConfig.uiProps.Headers);
          }

          if (response?.data?.sdkConfig?.uiProps?.showFooter !== undefined) {
            setShowFooter(response?.data?.sdkConfig?.uiProps?.showFooter);
          }
        }
      } catch (error) {
        setQuestData(null);
      } finally {
        setLoading(false);
      }
    };

    initializeFeedback();
  }, []);

  useEffect(() => {
    if (questData?.data?.actions[0]?.metadata?.ratingType) {
      const ratingType = questData.data.actions[0].metadata.ratingType;
      if (componentMapping[ratingType]) {
        setComponentType(ratingType);
      } else {
        console.error("Invalid rating type received:", ratingType);
      }
    }
  }, [questData]);

  useEffect(() => {
    if (themeConfig) themeManager.updateThemeConfig(themeConfig);
    if (styleConfig) themeManager.updateStyleConfig(styleConfig);
    if (questThemeData) themeManager.updateQuestTheme(questThemeData);
    if (brandTheme) themeManager.updateBrandTheme(brandTheme);
  }, [themeConfig, styleConfig, questThemeData, brandTheme]);

  const handleRatingClick = async (data: any) => {
    general.trackEvent(
      'quest-inlinefeedback-rating-click',
      (uniqueUserId || userId || user.userId)!,
      {data},
      true,
      !uniqueUserId
    )
    try {
      const userAuth = await inlineFeedbackApi.getUserAuth({
        apiKey,
        entityId,
        uniqueUserId,
        uniqueEmailId,
        userId,
        token,
        entityAuthenticationToken,
      });

      if (!userAuth) return;

      const headers = {
        apiKey,
        apisecret: apiSecret,
        userId: userAuth.userId,
        token: userAuth.token,
      };

      const actionId = questData?.data?.actions?.[0].actionId;
      const answer =
        componentType === "like"
          ? data?.like
            ? "like"
            : "dislike"
          : `${data.rate}`;

      const apiData = {
        actionId,
        answer,
        campaignVariationId: questData?.data.campaignVariationId,
      };

      const success = await inlineFeedbackApi.submitFeedback(
        entityId,
        campaignId,
        headers,
        apiData
      );

      if (success) {
        setShowSuccess(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onDataChange = (updatedData: object) => {
    data.current = updatedData;
    handleRatingClick(updatedData);
    onChange?.(updatedData);
  };

  const renderHeader = () => (
    <div className="feedbackHeader">
      <div className="inline-text-cont" style={{ background: "transparent" }}>
        <div className="inline-head" style={themeManager.getHeadingStyles()}>
          {header?.[0]?.heading || heading}
        </div>
        <div
          className="inline-desc"
          style={themeManager.getDescriptionStyles()}
        >
          {header?.[0]?.subHeading || description}
        </div>
      </div>
      <div onClick={onRequestClose} className="inline-cross-btn">
        {cross(themeManager.getCloseIconColor())}
      </div>
    </div>
  );

  const renderFeedbackSection = () => (
    <section className="feedbackSection">
      <Component
        onChange={onDataChange}
        count={count}
        style={themeManager.getActionStyles().container}
        buttonStyle={themeManager.getActionStyles().button}
        selectedButtonStyle={themeManager.getActionStyles().selectedButton}
        iconStyle={themeManager.getActionStyles().icon}
        selectedIconStyle={themeManager.getActionStyles().selectedIcon}
        initialState={initialState}
      />
      <div className="feedbackMessage">
        <span style={{ color: brandTheme?.titleColor }}>
          {questData?.data?.actions?.[0]?.metadata?.leftRatingText ||
            "Not satisfied"}
        </span>
        <span style={{ color: brandTheme?.titleColor }}>
          {questData?.data?.actions?.[0]?.metadata?.rightRatingText ||
            "Very satisfied"}
        </span>
      </div>
    </section>
  );

  const renderFooter = () => (
    <div
      className="feedbackWatermark"
      onClick={() => window.open(footerLink || "https://www.plgos.com/")}
      style={themeManager.getFooterStyles()}
    >
      <p>{footerText || "Powered by PLG OS"}</p>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 0V4L8 8V4H4V6.64083C4 7.39167 4.60833 8 5.35917 8H8L4 12C1.79083 12 0 10.2092 0 8V0H12Z"
          fill="#B9B9B9"
        />
        <path d="M12 8L8 8L8 12H12V8Z" fill="#B9B9B9" />
      </svg>
    </div>
  );

  const renderContent = () => (
    <div
      className="feedbackContainer"
      data-type={componentType.toString()}
      style={themeManager.getContainerStyles()}
    >
      {showSuccess ? (
        <Success closeFeedback={() => { setShowSuccess(false); onRequestClose?.() }} themeManager={themeManager} />
      ) : (
        <>
          {renderHeader()}
          {renderFeedbackSection()}
        </>
      )}
      {showFooter && renderFooter()}
      {isLoading && <Loader />}
    </div>
  );

  return questData ? renderContent() : null;
}

export default InlineFeedbackWrapper;
